import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { resendVerification, getVerificationStatus, refreshTokens, getRefreshToken } from '../api/client'

export function VerificationBanner() {
  const { user, refreshUser } = useAuth()
  const [verificationRequired, setVerificationRequired] = useState(false)
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')

  useEffect(() => {
    getVerificationStatus()
      .then((data) => setVerificationRequired(data.required))
      .catch(() => {})
  }, [])

  // Poll for verification status — handles the case where the user verified
  // in a different browser/tab. Refreshes the token (which reads email_verified
  // from the DB) and updates the auth context if verified.
  const checkVerified = useCallback(async () => {
    const rt = getRefreshToken()
    if (!rt) return
    try {
      await refreshTokens(rt)
      await refreshUser()
    } catch { /* ignore */ }
  }, [refreshUser])

  useEffect(() => {
    if (!verificationRequired || !user || user.email_verified) return
    const interval = setInterval(checkVerified, 15000) // every 15s
    return () => clearInterval(interval)
  }, [verificationRequired, user, checkVerified])

  // Don't show if verification is not enabled, user is verified, or no user
  if (!verificationRequired || !user || user.email_verified) {
    return null
  }

  const handleResend = async () => {
    setResendStatus('sending')
    try {
      await resendVerification()
      setResendStatus('sent')
    } catch {
      setResendStatus('error')
    }
  }

  return (
    <div className="bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-800">
      <div className="max-w-7xl mx-auto px-4 py-2 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-yellow-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <p className="text-sm text-yellow-800 dark:text-yellow-300">
              Please verify your email address. Some features are restricted until verified.
            </p>
          </div>
          <button
            onClick={handleResend}
            disabled={resendStatus === 'sending' || resendStatus === 'sent'}
            className="text-sm font-medium text-yellow-800 dark:text-yellow-300 underline hover:no-underline disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {resendStatus === 'sent' ? 'Email sent!' : resendStatus === 'sending' ? 'Sending...' : 'Resend verification email'}
          </button>
        </div>
      </div>
    </div>
  )
}
